import * as React from "react";
import {useState} from "react";
import {BraintreePayPalButtons, usePayPalScriptReducer} from "@paypal/react-paypal-js";
import {useCheckoutContext} from "../../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import {CreateBillingAgreementActions} from "@paypal/react-paypal-js/dist/types/types/braintreePayPalButtonTypes";
import {BraintreePayPalCheckoutTokenizationOptions} from "@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout";
import {getPaymentOptions} from "./helpers/paypalHelperFunctions";
import {INTERNAL_ERROR_TYPE} from "../PaymentController";
import {PayPalLoadingSpinner, buttonStyleObjectSummary} from "./BraintreeButton";
import {ErrorPlaceInfoType, onErrorClosure} from "./helpers/onErrorClosure";
import {purchase} from "../purchase";
import {ButtonPropType} from "./helpers/paypalTypes";
import {FUNDING_SOURCE} from "@paypal/paypal-js";
import {useCustomerSelector} from "../../../../store";

const PayPalSingleButton: React.FC<ButtonPropType> = ({handleError}) => {
  const {customer} = useCustomerSelector();
  const {checkout} = useCheckoutContext();
  const [{isRejected}] = usePayPalScriptReducer();
  const [isInited, setIsInited] = useState<boolean>(false);

  if (isRejected) {
    return null;
  }

  const onError = onErrorClosure(handleError, "PayPal single button");

  const createOrder = async (data, actions: CreateBillingAgreementActions) => {
    if (!checkout.deliveryAddress) {
      const noDeliveryError = new Error("Shipping line1 format is invalid.");
      (noDeliveryError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PAYPAL.OnCreateOrder;
      // Regular onError call
      onError(noDeliveryError);
      // Manual throw. See `PayPalExpressButtons.tsx`
      throw noDeliveryError;
    }

    try {
      const paymentOptions = getPaymentOptions({
        apiShippingOptions: undefined,
        deliveryAddress: checkout.deliveryAddress,
        cartData: checkout.cartData,
        currentLogisticsTypeId: checkout.logisticsTypeId,
      });

      return await actions.braintree.createPayment(paymentOptions);
    } catch (e) {
      (e as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PAYPAL.OnCreateOrder;
      // Regular onError call
      onError(e as Error | Record<string, unknown>);
      // Manual throw. See `PayPalExpressButtons.tsx`
      throw e;
    }
  };

  const onApprove = async (
    data: BraintreePayPalCheckoutTokenizationOptions,
    actions: CreateBillingAgreementActions,
  ) => {
    try {
      const tokenized = await actions.braintree.tokenizePayment(data);
      await purchase({
        checkout,
        nonce: tokenized.nonce,
        visitorId: customer.visitorId,
        paymentSource: (data as unknown as {paymentSource: FUNDING_SOURCE | undefined}).paymentSource,
      });
    } catch (err) {
      (err as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PAYPAL.OnApprove;
      onError(err as Error | Record<string, unknown>);
    }
  };

  return (
    <React.Fragment>
      {!isInited && <PayPalLoadingSpinner classes="bottom-margin" />}
      <BraintreePayPalButtons
        style={buttonStyleObjectSummary}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        onInit={() => setIsInited(true)}
      />
    </React.Fragment>
  );
};

export default PayPalSingleButton;
